import {
    CanvasPrimitive,
    CanvasPrimitiveType,
    CanvasPoint,
} from "@ltypes/scene";

// The standard Lumeba panel resolution (firmware/include/config.h).
export const DEVICE_WIDTH = 240;
export const DEVICE_HEIGHT = 320;

// Adafruit GFX base glyph cell is 6x8 px, scaled by `size`.
export const GLYPH_W = 6;
export const GLYPH_H = 8;

// Editor-only wrapper: the persisted format has no id, so we attach a stable
// local id for React keys / selection and strip it before sending upstream.
export type EditorPrimitive = CanvasPrimitive & { _id: string };

let idCounter = 0;
export function nextId(): string {
    idCounter += 1;
    return `p${Date.now().toString(36)}_${idCounter}`;
}

export function withIds(primitives: Array<CanvasPrimitive>): Array<EditorPrimitive> {
    return primitives.map((p) => ({ ...p, _id: nextId() } as EditorPrimitive));
}

export function stripIds(primitives: Array<EditorPrimitive>): Array<CanvasPrimitive> {
    return primitives.map(({ _id, ...rest }) => rest as CanvasPrimitive);
}

export const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));
export const round = (v: number): number => Math.round(v);

// Types offered in the palette. `pixel` is intentionally excluded (too fiddly
// to place by hand) but stays supported in the type/renderer for scenes that
// may already contain one.
export const PRIMITIVE_TYPES: Array<CanvasPrimitiveType> = [
    'text', 'rectangle', 'circle', 'line', 'triangle', 'arc',
];

const DEFAULT_COLOR = '#ffffff';

// Build a fresh primitive of the given type, anchored around a drop point
// (device coordinates). Values stay inside the panel where it makes sense.
export function createPrimitive(type: CanvasPrimitiveType, at: CanvasPoint): EditorPrimitive {
    const x = round(at.x);
    const y = round(at.y);
    const base = { _id: nextId(), color: DEFAULT_COLOR };

    switch (type) {
        case 'rectangle':
            return { ...base, type, position: { x: x - 30, y: y - 20 }, width: 60, height: 40, radius: 0 };
        case 'circle':
            return { ...base, type, position: { x, y }, radius: 24 };
        case 'arc':
            return { ...base, type, position: { x, y }, radius: 28, thickness: 4, start_angle: 0, end_angle: 270 };
        case 'pixel':
            return { ...base, type, position: { x, y } };
        case 'line':
            return { ...base, type, pointA: { x: x - 30, y }, pointB: { x: x + 30, y }, thickness: 2 };
        case 'triangle':
            return { ...base, type, pointA: { x, y: y - 24 }, pointB: { x: x + 28, y: y + 20 }, pointC: { x: x - 28, y: y + 20 } };
        case 'text':
        default:
            return { ...base, type: 'text', position: { x, y }, text: '{{time:HH:mm}}', size: 3, wrap: false };
    }
}

export type BBox = { x: number, y: number, w: number, h: number };

// Axis-aligned bounding box in device coordinates (used for selection +
// move/resize math). For text we estimate width from the raw token string;
// the rendered (resolved) width may differ slightly but that is fine for hit
// testing.
export function boundingBox(p: CanvasPrimitive): BBox {
    switch (p.type) {
        case 'rectangle':
            return { x: p.position.x, y: p.position.y, w: p.width, h: p.height };
        case 'circle':
        case 'arc':
            return { x: p.position.x - p.radius, y: p.position.y - p.radius, w: p.radius * 2, h: p.radius * 2 };
        case 'pixel':
            return { x: p.position.x, y: p.position.y, w: 1, h: 1 };
        case 'line': {
            const x = Math.min(p.pointA.x, p.pointB.x);
            const y = Math.min(p.pointA.y, p.pointB.y);
            return { x, y, w: Math.abs(p.pointA.x - p.pointB.x), h: Math.abs(p.pointA.y - p.pointB.y) };
        }
        case 'triangle': {
            const xs = [p.pointA.x, p.pointB.x, p.pointC.x];
            const ys = [p.pointA.y, p.pointB.y, p.pointC.y];
            const minX = Math.min(...xs), minY = Math.min(...ys);
            return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY };
        }
        case 'text':
        default: {
            const t = p as Extract<CanvasPrimitive, { type: 'text' }>;
            return { x: t.position.x, y: t.position.y, w: Math.max(1, t.text.length * GLYPH_W * t.size), h: GLYPH_H * t.size };
        }
    }
}

// Return a copy of the primitive translated by (dx, dy) device units.
export function movePrimitive<T extends CanvasPrimitive>(p: T, dx: number, dy: number): T {
    const shift = (pt: CanvasPoint): CanvasPoint => ({ x: round(pt.x + dx), y: round(pt.y + dy) });
    switch (p.type) {
        case 'line':
            return { ...p, pointA: shift(p.pointA), pointB: shift(p.pointB) };
        case 'triangle':
            return { ...p, pointA: shift(p.pointA), pointB: shift(p.pointB), pointC: shift(p.pointC) };
        default:
            return { ...p, position: shift((p as any).position) };
    }
}

// A short, human-friendly label for a layer row (text shows its content).
export function resolveLabel(p: CanvasPrimitive, fallback: string): string {
    if (p.type === 'text') {
        const trimmed = p.text.trim();
        return trimmed.length > 0 ? trimmed : fallback;
    }
    return fallback;
}

export const PRIMITIVE_LABEL_KEY: Record<CanvasPrimitiveType, string> = {
    text: 'page.scenes.canvas.tool.text',
    rectangle: 'page.scenes.canvas.tool.rectangle',
    circle: 'page.scenes.canvas.tool.circle',
    line: 'page.scenes.canvas.tool.line',
    triangle: 'page.scenes.canvas.tool.triangle',
    arc: 'page.scenes.canvas.tool.arc',
    pixel: 'page.scenes.canvas.tool.pixel',
};
